import {
  Box,
  Button,
  Grid,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import { isEmpty } from 'lodash';
import { format } from 'date-fns';
import useTable from 'src/hooks/useTable';
import { capitalCase } from 'change-case';
import Iconify from 'src/components/Iconify';
import { useState } from 'react';
import { PATH_DASHBOARD } from 'src/routes/paths';
import { getTimeSince } from 'src/utils/formatTime';
import { ReferralDto } from 'src/crs/referral/dto/referral.dto';
import AddReferral from '../../inbound-referral/AddReferral';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useServiceRequests } from 'src/careflow/ph-core/fhir-client/fhir-react';
import { TABLE_HEAD_CHILD_REFERRAL } from 'src/careflow/common/table-head';
import { TableHeadCustom, TableMoreMenu } from 'src/components/table';
import { WrappedPatient } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Patient';
import { ServiceRequestWrapper, WrappedServiceRequest } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/ServiceRequest';
import ReferralViewModel from '../utils/ReferralViewModel';
import useLocales from 'src/careflow/ph-core/internationalization/hook/useLocales';
import { translateTableHead } from 'src/careflow/helpers/common';
import { Add } from '@mui/icons-material';

type Props = {
  patient?: WrappedPatient | null;
  serviceRequest?: WrappedServiceRequest | null;
  referrals?: ReferralDto[];
};

export default function ReferralHistory({ patient, serviceRequest }: Props) {
  const [openAdd, setOpenAdd] = useState(false);
  const { i18n } = useLocales();

  const { page, rowsPerPage, onChangePage, onChangeRowsPerPage } = useTable({});

  const [serviceRequestChild, { refresh: refreshServiceRequestChild }] =
    useServiceRequests({
      filter: { 'based-on': serviceRequest?.id }, autofetch: !!serviceRequest?.id,
      map: ServiceRequestWrapper,
    });


  const handleClose = () => {
    setOpenAdd(false);
    refreshServiceRequestChild();
  };

  return (
    <>
      <Grid
        container
        display={'flex'}
        alignItems={'center'}
        paddingRight={2}
      >
        <Grid item xs={12} display={'flex'} justifyContent={'flex-end'}>
          <Button
            size="small"
            style={{ position: 'absolute', top: '25px', right: '25px' }}
            sx={{ height: '36px' }}
            startIcon={<Add />}
            onClick={() => setOpenAdd(true)}
          >
            {i18n('referral.details.secondaryReferrals.button', 'crs')}
          </Button>
        </Grid>
      </Grid>
      <TableContainer>
        <Table size="small" sx={{ mb: 2 }}>
          <TableHeadCustom headLabel=
            {translateTableHead(TABLE_HEAD_CHILD_REFERRAL, i18n, 'crs')} />
          <TableBody>
            {!isEmpty(serviceRequestChild) ? (
              serviceRequestChild?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row, index:number) => (
                  <ReferralHistoryItem
                    key={row.id || index }
                    referral={row}
                    patient={patient}
                    refreshData={refreshServiceRequestChild}
                    title={i18n('referral.details.secondaryReferrals.title', 'crs')}
                  />
                ))
            ) : (
              <TableRow>
                <TableCell colSpan={TABLE_HEAD_CHILD_REFERRAL?.length}>
                  <Typography variant="body2" align="center"> {i18n('referral.details.secondaryReferrals.noRows', 'crs')}</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Box sx={{ position: 'relative' }}>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={serviceRequestChild ? serviceRequestChild.length : 0}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={onChangePage}
          onRowsPerPageChange={onChangeRowsPerPage}
        />
      </Box>

      <AddReferral
        title={i18n('referral.details.secondaryReferrals.titleModal', 'crs')}
        openAdd={openAdd}
        patient={patient}
        serviceRequest={serviceRequest}
        onCancel={handleClose}
        observations={null}
        conditions={null}
      />
    </>
  );
}

export type ReferralItemProps = {
  referral?: WrappedServiceRequest | null;
  patient?: WrappedPatient | null;
  refreshData: () => void;
  title: string;
};

const ReferralHistoryItem = ({ referral, patient, refreshData, title }: ReferralItemProps) => {

  const [openEdit, setOpenEdit] = useState(false);
  const [editData, setEditData] = useState<ReferralViewModel>();
  const [openMenu, setOpenMenuActions] = useState<HTMLElement | null>(null);

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
    setOpenMenuActions(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setOpenMenuActions(null);
  };

  const handleClose = () => {
    setOpenEdit(false);
    refreshData();
    setEditData(undefined);
  };

  const handleEditData = (data: any) => {
    setEditData(data);
    setOpenEdit(true);
  };

  return (
    <TableRow>
        <TableCell style={{ cursor: 'pointer' }} sx={{ fontSize: `0.75rem` }}>
          {referral?.status && capitalCase(referral.status)}
        </TableCell>
        <TableCell style={{ cursor: 'pointer' }} sx={{ fontSize: `0.75rem` }}>
          {referral?.performer && referral.getPerfomerByType('Practitioner')?.display}
        </TableCell>
        <TableCell style={{ cursor: 'pointer' }} sx={{ fontSize: `0.75rem` }}>
          {referral?.occurrenceDateTime &&
            `${format(new Date(referral.occurrenceDateTime), 'MMM dd, yyyy')} (${getTimeSince(
              new Date(referral.occurrenceDateTime)
            )})`}
        </TableCell>
        <TableCell style={{ cursor: 'pointer' }} sx={{ fontSize: `0.75rem` }}>
          {referral?.code?.coding?.[0]?.code}
        </TableCell>
        <TableCell style={{ cursor: 'pointer' }} sx={{ fontSize: `0.75rem` }}>
          {referral?.requester && referral.requester.display}
        </TableCell>
        <TableCell style={{ cursor: 'pointer' }} sx={{ fontSize: `0.75rem` }}>
          {referral?.performer && referral.getPerfomerByType('Organization')?.display}
        </TableCell>
        <TableCell style={{ cursor: 'pointer' }} sx={{ fontSize: `0.75rem` }}>
          {referral?.locationReference && referral.locationReference?.[0]?.display}
        </TableCell>
        <TableCell align="right">
          <TableMoreMenu
            open={openMenu}
            onOpen={handleOpenMenu}
            onClose={handleCloseMenu}
            actions={[
              <MenuItem key="edit" onClick={() => handleEditData(referral)}>
                <Iconify icon={'mdi:pencil'} />
                Edit
              </MenuItem>,
              <MenuItem
                key="go"
                component="a"
                href={`${PATH_DASHBOARD.crs.childReferral}/${referral?.id}`}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ color: 'black', textDecoration: 'none' }}
              >
                <OpenInNewIcon sx={{ color: 'black' }} />
                <Typography>Go</Typography>
              </MenuItem>,
            ]}
          />
          <AddReferral
            title={title}
            openAdd={openEdit}
            referral={editData}
            patient={patient}
            onCancel={handleClose}
            observations={null}
            conditions={null}
          />
        </TableCell>
      </TableRow>
  )
};
